import React, { forwardRef } from "react";
import DatePicker from "react-datepicker";
import { TextField } from "@material-ui/core";
import "react-datepicker/dist/react-datepicker.css";
import "./SchoolDatePicker.css";
import { resolveSchoolDateFormat } from "../../utils/dateFormat";

/** Maps our school dateFormat tokens (DD-MM-YYYY etc.) to date-fns v2 format tokens react-datepicker expects. */
const toDateFnsFormat = (schoolDateFormat) => {
  switch (schoolDateFormat) {
    case "MM-DD-YYYY":
      return "MM-dd-yyyy";
    case "YYYY-MM-DD":
      return "yyyy-MM-dd";
    case "DD-MM-YYYY":
    default:
      return "dd-MM-yyyy";
  }
};

const pad2 = (n) => String(n).padStart(2, "0");

const CURRENT_YEAR = new Date().getFullYear();
const DEFAULT_MIN_DATE = new Date(CURRENT_YEAR - 100, 0, 1);
const DEFAULT_MAX_DATE = new Date(CURRENT_YEAR + 10, 11, 31);

/** Local YYYY-MM-DD string (no UTC shift) -> Date, for react-datepicker's `selected`. */
const isoToLocalDate = (iso) => {
  if (!iso || typeof iso !== "string") return null;
  const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const [, y, mo, d] = m;
  const date = new Date(Number(y), Number(mo) - 1, Number(d));
  return Number.isNaN(date.getTime()) ? null : date;
};

/** Date -> local YYYY-MM-DD string (no UTC shift), the same shape the native input already emitted. */
const dateToIso = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

/**
 * Drop-in replacement for `<TextField type="date">` whose displayed/typed format follows
 * the current school's configured dateFormat (unlike the native picker, which always shows
 * the OS/browser's own locale format regardless of what's configured).
 *
 * `value`/`onChange` still speak plain ISO "YYYY-MM-DD" strings, exactly like the native
 * input did, so callers (formik, etc.) don't need to change their state shape — only the
 * import and the change handler wiring (onChange receives a string directly, not an event).
 */
const SchoolDatePicker = ({
  id,
  name,
  label,
  value,
  onChange,
  onBlur,
  required,
  error,
  helperText,
  minDate,
  maxDate,
  disabled,
  fullWidth = true,
  schoolDateFormat = resolveSchoolDateFormat(),
  size = "small",
  variant = "outlined",
}) => {
  const CustomInput = forwardRef((inputProps, ref) => (
    <TextField
      {...inputProps}
      inputRef={ref}
      id={id}
      name={name}
      label={label}
      required={required}
      error={error}
      helperText={helperText}
      fullWidth={fullWidth}
      size={size}
      variant={variant}
      disabled={disabled}
      InputLabelProps={{ shrink: true }}
    />
  ));
  CustomInput.displayName = "SchoolDatePickerInput";

  return (
    <DatePicker
      id={id}
      name={name}
      selected={isoToLocalDate(value)}
      onChange={(date) => onChange(dateToIso(date))}
      onBlur={onBlur}
      dateFormat={toDateFnsFormat(schoolDateFormat)}
      // Default to a 100-year window back (plus 10 ahead for future dates like due dates)
      // so the Year dropdown lists enough years; explicit min/max still take priority.
      minDate={isoToLocalDate(minDate) || DEFAULT_MIN_DATE}
      maxDate={isoToLocalDate(maxDate) || DEFAULT_MAX_DATE}
      disabled={disabled}
      customInput={<CustomInput />}
      autoComplete="off"
      // Month/year dropdowns in the calendar header so users can jump years (e.g. for DOB).
      showMonthDropdown
      showYearDropdown
      dropdownMode="select"
      // Render the calendar popup in a body-level portal with fixed positioning so it
      // isn't clipped/overlapped by ancestor MUI Dialogs (which set their own overflow
      // and z-index stacking context) — see .school-date-picker-popper CSS below.
      portalId="school-date-picker-portal"
      popperClassName="school-date-picker-popper"
      popperProps={{ strategy: "fixed" }}
    />
  );
};

export default SchoolDatePicker;
